import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { sanitizeHtml } from "@/lib/sanitize";
import { useAuth } from "@/context/AuthContext";
import Infobox from "@/components/Infobox";
import { LabelBadge } from "@/components/LabelBadge";
import { ArcButton } from "@/components/ArcButton";
import { Editable } from "@/components/Editable";
import { ChevronRight, Pencil, Trash2, ExternalLink, Edit3 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function slugifyHeading(h) {
  return h.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export default function Article() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAdmin } = useAuth();
  const [article, setArticle] = useState(undefined);
  const [related, setRelated] = useState([]);
  const [delOpen, setDelOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setArticle(undefined);
    api.get(`/articles/${slug}`)
      .then(async (r) => {
        setArticle(r.data);
        const rels = await Promise.all(
          (r.data.related || []).slice(0, 5).map((s) => api.get(`/articles/${s}`).then((x) => x.data).catch(() => null))
        );
        setRelated(rels.filter(Boolean));
      })
      .catch(() => setArticle(null));
  }, [slug]);

  if (article === undefined) return <div className="flex min-h-[50vh] items-center justify-center text-sm" style={{ color: "var(--arc-text-2)" }}>Loading…</div>;
  if (article === null) return (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="font-serif-arc text-3xl font-black">Article not found</h1>
      <p className="mt-3 text-sm" style={{ color: "var(--arc-text-2)" }}>This entry has not yet been documented.</p>
      <div className="mt-6 flex justify-center"><ArcButton to="/" variant="outline">Return home</ArcButton></div>
    </div>
  );

  const belongsTo = (article.encyclopedias && article.encyclopedias.length) ? article.encyclopedias : [article.encyclopedia];
  const fromEnc = searchParams.get("enc");
  const activeEnc = (fromEnc && belongsTo.includes(fromEnc)) ? fromEnc : (belongsTo[0] || "ghoulax");
  const isGhoulax = activeEnc === "ghoulax";
  const accent = isGhoulax ? "ghoulax" : "zyfley";
  const encName = isGhoulax ? "Ghoulax Archives" : "Zyfley Encyclopedia";
  const encHome = isGhoulax ? "/ghoulax" : "/zyfley";

  const doDelete = async () => {
    try { await api.delete(`/articles/${slug}`); toast.success("Article deleted"); navigate(encHome); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
      {/* Breadcrumb */}
      <nav data-testid="breadcrumb" className="flex flex-wrap items-center gap-1 text-sm" style={{ color: "var(--arc-text-2)" }}>
        <Link to="/" className="hover:underline">Home</Link>
        <ChevronRight size={14} />
        <Link to={encHome} className="hover:underline">{encName}</Link>
        <ChevronRight size={14} />
        <span className="font-medium" style={{ color: "var(--arc-text)" }}>{article.title}</span>
      </nav>

      <div className="mt-6 flex flex-col gap-8 lg:flex-row">
        {/* Main column */}
        <article className="min-w-0 flex-1 order-2 lg:order-1">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b pb-4" style={{ borderColor: isGhoulax ? "var(--ghoulax)" : "var(--zyfley)" }}>
            <div>
              <h1 className={`font-serif-arc text-3xl font-black tracking-tight sm:text-4xl ${isGhoulax ? "ghoulax-accent" : "zyfley-accent"}`}>{article.title}</h1>
              {article.subtitle && <p className="mt-2 text-sm italic" style={{ color: "var(--arc-text-2)" }}>{article.subtitle}</p>}
              {article.redirected_from && (
                <p className="mt-1 text-xs" style={{ color: "var(--arc-text-2)" }} data-testid="redirect-note">
                  (Redirected from <span className="italic">/{article.redirected_from}</span>)
                </p>
              )}
            </div>
            {article.badge && <LabelBadge label={article.badge} className="mt-2" />}
          </div>

          {/* Admin + Suggest edit toolbar */}
          <div className="mt-4 flex flex-wrap gap-2">
            <ArcButton to={`/suggest-edit?article=${encodeURIComponent(article.title)}&slug=${article.slug}`} variant="ghost" className="border" data-testid="suggest-edit-btn">
              <Edit3 size={15} /> Suggest an Edit
            </ArcButton>
            {isAdmin && (
              <>
                <ArcButton to={`/admin/articles/${article.slug}`} variant="ghost" className="border" data-testid="admin-edit-article"><Pencil size={15} /> Edit</ArcButton>
                <ArcButton variant="ghost" className="border text-red-600" onClick={() => setDelOpen(true)} data-testid="admin-delete-article"><Trash2 size={15} /> Delete</ArcButton>
              </>
            )}
          </div>

          {/* Mobile infobox */}
          {article.infobox && <div className="mt-6 lg:hidden"><Infobox infobox={article.infobox} accent={accent} /></div>}

          {article.slug === "glazing-index" && (
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-md border-2 p-4" style={{ borderColor: "var(--ghoulax)", backgroundColor: "var(--ghoulax-soft)" }}>
              <p className="text-sm font-semibold">Wondering where you rank? Take the scientifically meaningless quiz.</p>
              <ArcButton to="/quiz/glazing" variant="ghoulax" data-testid="glazing-quiz-cta">Take the Glazing Quiz →</ArcButton>
            </div>
          )}

          {/* Intro */}
          {article.intro && <div className="article-body mt-6" dangerouslySetInnerHTML={{ __html: sanitizeHtml(article.intro) }} />}

          {/* Contents */}
          {article.sections?.length > 0 && (
            <nav data-testid="contents-box" className="mt-6 inline-block rounded-md border p-4" style={{ backgroundColor: "var(--arc-card)" }}>
              <p className="mb-2 font-serif-arc text-sm font-bold">Contents</p>
              <ol className="space-y-1 text-sm">
                {article.sections.map((s, i) => (
                  <li key={s.id || i}>
                    <a href={`#${slugifyHeading(s.heading)}`} className={isGhoulax ? "ghoulax-accent hover:underline" : "zyfley-accent hover:underline"}>
                      {i + 1}. {s.heading}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          )}

          {/* Sections */}
          <div className="mt-8 space-y-8">
            {article.sections?.map((s, i) => (
              <section key={s.id || i} id={slugifyHeading(s.heading)}>
                <div className="mb-3 flex flex-wrap items-center gap-3 border-b pb-2">
                  <h2 className="font-serif-arc text-2xl font-bold">{s.heading}</h2>
                  {s.label && <LabelBadge label={s.label} />}
                </div>
                <div className="article-body" dangerouslySetInnerHTML={{ __html: sanitizeHtml(s.body || "") }} />
              </section>
            ))}
          </div>

          {/* Timeline */}
          {article.timeline?.length > 0 && (
            <section id="timeline" className="mt-10">
              <h2 className="mb-4 font-serif-arc text-2xl font-bold border-b pb-2">Timeline</h2>
              <ol className="relative ml-3 border-l-2 pl-6" style={{ borderColor: isGhoulax ? "var(--ghoulax)" : "var(--zyfley)" }}>
                {article.timeline.map((t, i) => (
                  <li key={`${t.date}-${t.event}-${i}`} className="mb-8 last:mb-0" data-testid={`timeline-entry-${i}`}>
                    <span className="absolute -left-[9px] mt-1.5 h-4 w-4 rounded-full border-2" style={{ backgroundColor: "var(--arc-bg)", borderColor: isGhoulax ? "var(--ghoulax)" : "var(--zyfley)" }} />
                    <p className="text-xs font-bold uppercase tracking-wider" style={{ color: isGhoulax ? "var(--ghoulax)" : "var(--zyfley)" }}>{t.date}</p>
                    <h3 className="mt-1 font-serif-arc text-lg font-bold">{t.event}</h3>
                    {t.description && <p className="mt-1 text-sm leading-relaxed" style={{ color: "var(--arc-text-2)" }}>{t.description}</p>}
                    {t.source && <p className="mt-1 text-xs" style={{ color: "var(--arc-text-2)" }}>Source: {t.source}</p>}
                  </li>
                ))}
              </ol>
            </section>
          )}

          {/* References */}
          {article.references?.length > 0 && (
            <section className="mt-8 border-t pt-4">
              <h2 className="mb-3 font-serif-arc text-lg font-bold">References</h2>
              <ol className="list-decimal space-y-1 pl-5 text-sm" style={{ color: "var(--arc-text-2)" }}>
                {article.references.map((r, i) => (
                  <li key={`${r.title}-${i}`}>
                    {r.url ? <a href={r.url} target="_blank" rel="noreferrer" className="zyfley-accent hover:underline inline-flex items-center gap-1">{r.title} <ExternalLink size={12} /></a> : r.title}
                  </li>
                ))}
              </ol>
            </section>
          )}

          {/* Last updated (auto date, admin can override inline) */}
          {article.updated_at && (
            <p className="mt-8 text-xs" style={{ color: "var(--arc-text-2)" }} data-testid="article-last-updated">
              Last updated:{" "}
              <Editable
                id={`article.${article.slug}.updated`}
                as="span"
                fallback={new Date(article.updated_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
              />
            </p>
          )}

          {/* Related */}
          {related.length > 0 && (
            <section className="mt-10 border-t pt-6">
              <h2 className="mb-4 font-serif-arc text-lg font-bold">Related Articles</h2>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {related.map((r) => (
                  <Link key={r.slug} to={`/article/${r.slug}`} data-testid={`related-${r.slug}`} className="rounded-md border p-4 shadow-sm transition-shadow hover:shadow-md" style={{ backgroundColor: "var(--arc-card)" }}>
                    <span className={`font-serif-arc font-bold ${r.encyclopedia === "ghoulax" ? "ghoulax-accent" : "zyfley-accent"}`}>{r.title}</span>
                    <p className="mt-1 line-clamp-2 text-sm" style={{ color: "var(--arc-text-2)" }}>{r.description}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>

        {/* Desktop infobox */}
        {article.infobox && (
          <div className="order-1 w-full shrink-0 lg:order-2 lg:w-80 hidden lg:block">
            <div className="sticky top-20"><Infobox infobox={article.infobox} accent={accent} /></div>
          </div>
        )}
      </div>

      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent style={{ backgroundColor: "var(--arc-card)" }}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this article?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes "{article.title}" from the archive.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
